import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@arcadeai/design-system/components/ui/atoms/sidebar";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Link, useLocation, useMatchRoute } from "@tanstack/react-router";
import { memo } from "react";
import {
  useOrgPermissions,
  useProjectPermissions,
} from "@/app/capabilities/api/hooks";
import { useBadge } from "./hooks/use-badge";
import { useSidebarUrl } from "./hooks/use-sidebar-item";
import { NavMenuItem } from "./nav-menu-item";
import type { SidebarItem } from "./types";

type NavMenuProps = {
  items: SidebarItem[];
  extraItems?: React.ReactNode;
  label?: string;
};

const ParentMenuItem = ({
  item,
  isActive,
  asChild,
}: {
  item: SidebarItem;
  isActive: boolean;
  asChild?: boolean;
}) => {
  const badge = useBadge({ item, isActive });

  if (item.url) {
    return asChild ? (
      <SidebarMenuButton
        isActive={isActive}
        render={<Link to={item.url} />}
        tooltip={item.title}
      >
        {!!item.icon && <item.icon />}
        <span>{item.title}</span>
        {badge}
      </SidebarMenuButton>
    ) : (
      <Link to={item.url}>
        <SidebarMenuButton isActive={isActive} tooltip={item.title}>
          {!!item.icon && <item.icon />}
          <span>{item.title}</span>
          {badge}
        </SidebarMenuButton>
      </Link>
    );
  }

  return (
    <SidebarMenuButton tooltip={item.title}>
      {!!item.icon && <item.icon />}
      <span>{item.title}</span>
      {badge}
    </SidebarMenuButton>
  );
};

const CollapsedSubItem = ({
  item,
  isActive,
}: {
  item: SidebarItem;
  isActive: boolean;
}) => {
  const badge = useBadge({ item, isSubItem: true, isActive });

  if (item.url) {
    return (
      <Link to={item.url}>
        {({ isActive: routerIsActive }: { isActive: boolean }) => {
          // Use isActive (which accounts for activeOnChildRoutes) when applicable
          const finalIsActive = item.activeOnChildRoutes
            ? isActive
            : routerIsActive;
          return (
            <SidebarMenuButton
              className={cn(finalIsActive && "text-white")}
              isActive={isActive}
              tooltip={item.title}
            >
              {!!item.icon && <item.icon />}
              <span>{item.title}</span>
              {badge}
            </SidebarMenuButton>
          );
        }}
      </Link>
    );
  }

  return (
    <SidebarMenuButton tooltip={item.title}>
      {!!item.icon && <item.icon />}
      <span>{item.title}</span>
      {badge}
    </SidebarMenuButton>
  );
};

const ExpandedSubItem = ({
  item,
  isActive,
}: {
  item: SidebarItem;
  isActive: boolean;
}) => {
  const badge = useBadge({ item, isSubItem: true, isActive });

  if (item.url) {
    return (
      <SidebarMenuSubButton
        className={cn(isActive && "text-white")}
        isActive={isActive}
        render={<Link to={item.url} />}
      >
        {!!item.icon && <item.icon className="text-current!" />}
        <span>{item.title}</span>
        {badge}
      </SidebarMenuSubButton>
    );
  }

  return (
    <SidebarMenuSubButton className="pl-3">
      {!!item.icon && <item.icon className="size-4" />}
      <span>{item.title}</span>
      {badge}
    </SidebarMenuSubButton>
  );
};

export const NavMenu = memo(({ items, label, extraItems }: NavMenuProps) => {
  const { state } = useSidebar();
  const location = useLocation();
  const matchRoute = useMatchRoute();
  const currentPath = location.pathname;
  const resolveUrl = useSidebarUrl();
  const { canProject } = useProjectPermissions();
  const { canOrg, orgAuthzEnabled } = useOrgPermissions();
  // Show an item only if the caller holds its permission (no permission → always
  // show; an array of permissions is any-of).
  const canShow = (
    item: Pick<SidebarItem, "requiredOrgPermission" | "requiredPermission">
  ) => {
    const { requiredOrgPermission, requiredPermission } = item;
    let projectAllowed = true;
    if (requiredPermission) {
      projectAllowed = Array.isArray(requiredPermission)
        ? requiredPermission.some((permission) => canProject(permission))
        : canProject(requiredPermission);
    }

    let orgAllowed = true;
    if (requiredOrgPermission) {
      // Org-only surfaces don't exist when operator-RBAC is off (their route
      // 404s), and `canOrg` is allow-all when off — so require authz enabled too,
      // otherwise the nav link would always 404 with RBAC disabled.
      orgAllowed =
        orgAuthzEnabled &&
        (Array.isArray(requiredOrgPermission)
          ? requiredOrgPermission.some((permission) => canOrg(permission))
          : canOrg(requiredOrgPermission));
    }

    return projectAllowed && orgAllowed;
  };
  // Filter top-level items AND their sub-items by permission. A parent with
  // sub-items is shown when at least one sub-item remains visible — its own
  // permission must not hide sub-routes the caller can still reach. When every
  // sub-item is filtered out, keep the parent as a plain link if it has its own
  // route and passes its own gate, so a caller who can still reach the parent
  // route (e.g. /projects) does not lose the sidebar entry with the sub-items.
  const visibleItems = items.flatMap((item) => {
    if (item.items?.length) {
      const visibleSubItems = item.items.filter(canShow);
      if (visibleSubItems.length) {
        return [{ ...item, items: visibleSubItems }];
      }
      return item.url && canShow(item) ? [{ ...item, items: undefined }] : [];
    }
    return canShow(item) ? [item] : [];
  });
  const withResolvedUrls = (item: SidebarItem): SidebarItem => ({
    ...item,
    url: resolveUrl(item.url),
    activeBasePath: resolveUrl(item.activeBasePath),
    items: item.items?.map((subItem) => ({
      ...subItem,
      url: resolveUrl(subItem.url),
      activeBasePath: resolveUrl(subItem.activeBasePath),
    })),
  });

  // Custom active check for items that should be active on multiple routes
  const isItemActive = (item: SidebarItem) => {
    if (!item.url) {
      return false;
    }

    // If item should be active on child routes, check if current path starts with base path or item URL
    if (item.activeOnChildRoutes) {
      const basePath = item.activeBasePath ?? item.url;
      return currentPath.startsWith(basePath);
    }
    // For all other items, use matchRoute to check if active
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    return !!matchRoute({ to: item.url as string });
  };

  // Check if parent should show active state
  const isParentActive = (item: SidebarItem) => {
    // Parents without children use the normal active check
    if (!item.items?.length) {
      return isItemActive(item);
    }

    // Parents with children should show active only when:
    // 1. We're exactly on the parent's URL
    // 2. AND no child is currently active
    if (item.url) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const isOnParentRoute = !!matchRoute({ to: item.url as string });
      const anyChildActive = item.items.some((child) => isItemActive(child));
      return isOnParentRoute && !anyChildActive;
    }

    return false;
  };

  return (
    <SidebarGroup>
      {!!label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      <SidebarMenu>
        {visibleItems.map((rawItem) => {
          const item = withResolvedUrls(rawItem);
          // If no sub-items, render a simple menu item with link
          if (!item.items?.length) {
            return <NavMenuItem item={item} key={item.id} />;
          }

          // When collapsed, render parent and subitems as separate menu items
          if (state === "collapsed") {
            return (
              <div key={item.title}>
                <SidebarMenuItem>
                  <ParentMenuItem isActive={isParentActive(item)} item={item} />
                </SidebarMenuItem>
                {item.items?.map((subItem) => (
                  <SidebarMenuItem className="mt-1" key={subItem.title}>
                    <CollapsedSubItem
                      isActive={isItemActive(subItem)}
                      item={subItem}
                    />
                  </SidebarMenuItem>
                ))}
              </div>
            );
          }

          // When expanded, render parent with subitems in hierarchy
          return (
            <SidebarMenuItem key={item.title}>
              <ParentMenuItem
                asChild
                isActive={isParentActive(item)}
                item={item}
              />
              <SidebarMenuSub className="w-full">
                {item.items?.map((subItem) => (
                  <SidebarMenuSubItem
                    className="w-full pr-4"
                    key={subItem.title}
                  >
                    <ExpandedSubItem
                      isActive={isItemActive(subItem)}
                      item={subItem}
                    />
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
      {extraItems}
    </SidebarGroup>
  );
});
